import { apiClient } from '@/api/client';
import { Business, getStaticCategory, getStaticFilter } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { CheckboxGroup } from '@/components/ui/CheckboxGroup';
import { FormErrorMessage } from '@/components/ui/FormErrorMessage';
import { TabNavigation } from '@/components/ui/TabNavigation';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { useAuth } from '@/lib/auth';
import { queryKeys } from '@/lib/queryKeys';
import { BusinessFormData } from '@/pages/businesses/hooks/useBusinessMutations';
import { mdiCheck, mdiClose, mdiPlus, mdiStore, mdiTrashCan } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BusinessImagesTab } from './BusinessImagesTab';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

interface BusinessFormProps {
  business?: Business | null;
  onSubmit: (data: BusinessFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  onDeleteImage?: (businessId: number, imageId: number) => void;
  onSetPrimaryImage?: (businessId: number, imageId: number) => void;
  isDeletingImage?: boolean;
  isSettingPrimaryImage?: boolean;
}

const getFormValues = (business?: Business | null): BusinessFormData => ({
  name: business?.name || '',
  address: business?.address || '',
  postalCode: business?.postalCode || '',
  city: business?.city || '',
  province: business?.province || '',
  country: business?.country || '',
  website: business?.website || '',
  phone: business?.phone || '',
  status: business?.status || 'Pending',
  categoryIds: business?.businessCategories?.map(bc => bc.category?.id!) || [],
  filterIds: business?.filters?.map(bf => bf.filter?.id!) || [],
  openingHours:
    business?.openingHours?.map(oh => ({
      dayOfWeek: oh.dayOfWeek!,
      openTime: oh.openTime?.substring(0, 5) || '09:00',
      closeTime: oh.closeTime?.substring(0, 5) || '17:00',
    })) || [],
});

export const BusinessForm: React.FC<BusinessFormProps> = ({
  business,
  onSubmit,
  onCancel,
  loading = false,
  onDeleteImage,
  onSetPrimaryImage,
  isDeletingImage = false,
  isSettingPrimaryImage = false,
}) => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = React.useState<
    'basic' | 'location' | 'hours' | 'categories' | 'images'
  >('basic');

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
    setValue,
  } = useForm<BusinessFormData>({
    defaultValues: getFormValues(business),
    mode: 'onChange',
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: [queryKeys.ADMIN_CATEGORIES],
    queryFn: async () => {
      const response = await apiClient.GET('/category');
      if (response.error) throw new Error('Failed to fetch categories');
      return response.data || [];
    },
    enabled: isAuthenticated,
  });

  // Fetch filters
  const { data: filters } = useQuery({
    queryKey: [queryKeys.ADMIN_FILTERS],
    queryFn: async () => {
      const response = await apiClient.GET('/filter');
      if (response.error) throw new Error('Failed to fetch filters');
      return response.data || [];
    },
    enabled: isAuthenticated,
  });

  React.useEffect(() => {
    if (business) {
      reset(getFormValues(business));
    }
  }, [business, reset]);

  const openingHours = watch('openingHours');
  const selectedCategoryIds = watch('categoryIds');
  const selectedFilterIds = watch('filterIds');

  const handleFormSubmit = async (data: BusinessFormData) => {
    await onSubmit(data);
  };

  const toggleDay = (dayIndex: number) => {
    const existingHours = openingHours.filter(
      oh => Number(oh.dayOfWeek) === dayIndex,
    );
    if (existingHours.length > 0) {
      setValue(
        'openingHours',
        openingHours.filter(oh => Number(oh.dayOfWeek) !== dayIndex),
        { shouldDirty: true },
      );
    } else {
      setValue(
        'openingHours',
        [
          ...openingHours,
          { dayOfWeek: dayIndex, openTime: '09:00', closeTime: '17:00' },
        ],
        { shouldDirty: true },
      );
    }
  };

  const addTimePeriod = (dayIndex: number) => {
    setValue(
      'openingHours',
      [
        ...openingHours,
        { dayOfWeek: dayIndex, openTime: '09:00', closeTime: '17:00' },
      ],
      { shouldDirty: true },
    );
  };

  const removeTimePeriod = (dayIndex: number, periodIndex: number) => {
    const dayHours = openingHours.filter(
      oh => Number(oh.dayOfWeek) === dayIndex,
    );
    if (dayHours.length <= 1) {
      setValue(
        'openingHours',
        openingHours.filter(oh => Number(oh.dayOfWeek) !== dayIndex),
        { shouldDirty: true },
      );
    } else {
      let count = 0;
      setValue(
        'openingHours',
        openingHours.filter(oh => {
          if (Number(oh.dayOfWeek) === dayIndex) {
            const shouldKeep = count !== periodIndex;
            count++;
            return shouldKeep;
          }
          return true;
        }),
        { shouldDirty: true },
      );
    }
  };

  const updateHourTime = (
    dayIndex: number,
    periodIndex: number,
    field: 'openTime' | 'closeTime',
    value: string,
  ) => {
    let count = 0;
    setValue(
      'openingHours',
      openingHours.map(oh => {
        if (Number(oh.dayOfWeek) === dayIndex) {
          const shouldUpdate = count === periodIndex;
          count++;
          return shouldUpdate ? { ...oh, [field]: value } : oh;
        }
        return oh;
      }),
      { shouldDirty: true },
    );
  };

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='p-6 border-b border-grey-200'>
        <div className='flex items-center space-x-3'>
          <div className='w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center'>
            <svg
              viewBox='0 0 24 24'
              fill='currentColor'
              className='w-6 h-6 text-blue-600'
            >
              <path d={mdiStore} />
            </svg>
          </div>
          <div>
            <h3 className='text-lg font-semibold text-grey-900'>
              Edit Business
            </h3>
            <p className='text-sm text-grey-600'>
              Update business information and settings
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <TabNavigation
        tabs={[
          { id: 'basic', label: 'Basic Info' },
          { id: 'location', label: 'Location' },
          { id: 'hours', label: 'Hours' },
          { id: 'categories', label: 'Categories & Filters' },
          { id: 'images', label: 'Images', count: business?.images?.length },
        ]}
        activeTab={activeTab}
        onTabChange={tab =>
          setActiveTab(
            tab as 'basic' | 'location' | 'hours' | 'categories' | 'images',
          )
        }
        size='sm'
      />

      {/* Form Content */}
      {activeTab !== 'images' && (
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className='flex-1 overflow-y-auto p-6'
        >
          {activeTab === 'basic' && (
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-grey-700 mb-1'>
                  Business Name *
                </label>
                <Controller
                  name='name'
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='text'
                      className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.name ? 'border-red-300' : 'border-grey-300'
                      }`}
                      placeholder='Enter business name'
                    />
                  )}
                />
                <FormErrorMessage message={errors.name?.message} />
              </div>

              <div>
                <label className='block text-sm font-medium text-grey-700 mb-1'>
                  Status *
                </label>
                <Controller
                  name='status'
                  control={control}
                  rules={{ required: 'Status is required' }}
                  render={({ field }) => (
                    <select
                      {...field}
                      className='w-full px-3 py-2 border border-grey-300 rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    >
                      {STATUS_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-grey-700 mb-1'>
                  Website
                </label>
                <Controller
                  name='website'
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='url'
                      className='w-full px-3 py-2 border border-grey-300 rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      placeholder='https://example.com'
                    />
                  )}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-grey-700 mb-1'>
                  Phone
                </label>
                <Controller
                  name='phone'
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='tel'
                      className='w-full px-3 py-2 border border-grey-300 rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      placeholder='+1 (555) 123-4567'
                    />
                  )}
                />
              </div>
            </div>
          )}

          {activeTab === 'location' && (
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-grey-700 mb-1'>
                  Address *
                </label>
                <Controller
                  name='address'
                  control={control}
                  rules={{ required: 'Address is required' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='text'
                      className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.address ? 'border-red-300' : 'border-grey-300'
                      }`}
                      placeholder='123 Main St'
                    />
                  )}
                />
                <FormErrorMessage message={errors.address?.message} />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-grey-700 mb-1'>
                    City *
                  </label>
                  <Controller
                    name='city'
                    control={control}
                    rules={{ required: 'City is required' }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type='text'
                        className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.city ? 'border-red-300' : 'border-grey-300'
                        }`}
                        placeholder='City'
                      />
                    )}
                  />
                  <FormErrorMessage message={errors.city?.message} />
                </div>

                <div>
                  <label className='block text-sm font-medium text-grey-700 mb-1'>
                    Province/State
                  </label>
                  <Controller
                    name='province'
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type='text'
                        className='w-full px-3 py-2 border border-grey-300 rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        placeholder='Province'
                      />
                    )}
                  />
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-grey-700 mb-1'>
                    Postal Code *
                  </label>
                  <Controller
                    name='postalCode'
                    control={control}
                    rules={{ required: 'Postal code is required' }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type='text'
                        className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.postalCode
                            ? 'border-red-300'
                            : 'border-grey-300'
                        }`}
                        placeholder='12345'
                      />
                    )}
                  />
                  <FormErrorMessage message={errors.postalCode?.message} />
                </div>

                <div>
                  <label className='block text-sm font-medium text-grey-700 mb-1'>
                    Country *
                  </label>
                  <Controller
                    name='country'
                    control={control}
                    rules={{ required: 'Country is required' }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type='text'
                        className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.country ? 'border-red-300' : 'border-grey-300'
                        }`}
                        placeholder='Country'
                      />
                    )}
                  />
                  <FormErrorMessage message={errors.country?.message} />
                </div>
              </div>

              {business?.timezone && (
                <div className='mt-4 p-3 bg-grey-50 rounded-lg'>
                  <label className='block text-sm font-medium text-grey-700 mb-1'>
                    Timezone
                  </label>
                  <p className='text-sm text-grey-600'>{business.timezone}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'hours' && (
            <div className='space-y-4'>
              <p className='text-sm text-grey-600 mb-4'>
                Select days and set opening/closing times for each day. You can
                add multiple time periods per day (e.g., 9:00-14:00,
                17:00-21:00).
              </p>
              {DAY_NAMES.map((day, dayIndex) => {
                const dayHours = openingHours.filter(
                  oh => Number(oh.dayOfWeek) === dayIndex,
                );
                const isOpen = dayHours.length > 0;
                return (
                  <div
                    key={dayIndex}
                    className={`p-3 rounded-lg border ${isOpen ? 'bg-green-50 border-green-200' : 'bg-grey-50 border-grey-200'}`}
                  >
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center space-x-3'>
                        <ToggleSwitch
                          value={isOpen}
                          onChange={() => toggleDay(dayIndex)}
                        />
                        <span className='font-medium text-grey-900'>{day}</span>
                      </div>
                      {isOpen && (
                        <button
                          type='button'
                          onClick={() => addTimePeriod(dayIndex)}
                          className='flex items-center text-xs text-blue-600 hover:text-blue-800'
                        >
                          <svg
                            viewBox='0 0 24 24'
                            fill='currentColor'
                            className='w-4 h-4 mr-1'
                          >
                            <path d={mdiPlus} />
                          </svg>
                          Add period
                        </button>
                      )}
                    </div>
                    {isOpen && (
                      <div className='mt-3 space-y-2 pl-12'>
                        {dayHours.map((hours, periodIndex) => (
                          <div
                            key={periodIndex}
                            className='flex items-center space-x-2'
                          >
                            <input
                              type='time'
                              value={hours.openTime || '09:00'}
                              onChange={e =>
                                updateHourTime(
                                  dayIndex,
                                  periodIndex,
                                  'openTime',
                                  e.target.value,
                                )
                              }
                              className='px-2 py-1 border border-grey-300 rounded-md text-sm'
                            />
                            <span className='text-grey-500'>to</span>
                            <input
                              type='time'
                              value={hours.closeTime || '17:00'}
                              onChange={e =>
                                updateHourTime(
                                  dayIndex,
                                  periodIndex,
                                  'closeTime',
                                  e.target.value,
                                )
                              }
                              className='px-2 py-1 border border-grey-300 rounded-md text-sm'
                            />
                            <button
                              type='button'
                              onClick={() =>
                                removeTimePeriod(dayIndex, periodIndex)
                              }
                              className='p-1 text-grey-400 hover:text-red-600'
                              title='Remove time period'
                            >
                              <svg
                                viewBox='0 0 24 24'
                                fill='currentColor'
                                className='w-4 h-4'
                              >
                                <path d={mdiTrashCan} />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'categories' && (
            <div className='space-y-6'>
              <div>
                <h4 className='text-sm font-medium text-grey-700 mb-3'>
                  Categories
                </h4>
                <CheckboxGroup
                  options={
                    categories?.map(c => {
                      const staticCategory = getStaticCategory(c.name || '');
                      return {
                        value: c.id!,
                        label: `${staticCategory?.emoji || '📁'} ${staticCategory?.label || c.name}`,
                      };
                    }) || []
                  }
                  selectedValues={selectedCategoryIds}
                  onChange={ids =>
                    setValue('categoryIds', ids, { shouldDirty: true })
                  }
                  color='purple'
                />
              </div>

              <div>
                <h4 className='text-sm font-medium text-grey-700 mb-3'>
                  Filters
                </h4>
                <CheckboxGroup
                  options={
                    filters?.map(f => {
                      const staticFilter = getStaticFilter(f.name || '');
                      return {
                        value: f.id!,
                        label: `${staticFilter?.emoji || '🏷️'} ${staticFilter?.label || f.name}`,
                      };
                    }) || []
                  }
                  selectedValues={selectedFilterIds}
                  onChange={ids =>
                    setValue('filterIds', ids, { shouldDirty: true })
                  }
                  color='blue'
                />
              </div>
            </div>
          )}
        </form>
      )}

      {activeTab === 'images' &&
        business &&
        onDeleteImage &&
        onSetPrimaryImage && (
          <div className='flex-1 overflow-y-auto'>
            <BusinessImagesTab
              business={business}
              onDeleteImage={onDeleteImage}
              onSetPrimaryImage={onSetPrimaryImage}
              isDeletingImage={isDeletingImage}
              isSettingPrimaryImage={isSettingPrimaryImage}
            />
          </div>
        )}

      {/* Action Buttons */}
      <div className='p-6 border-t border-grey-200'>
        {activeTab === 'images' ? (
          <div className='flex items-center justify-between'>
            <p className='text-sm text-grey-500'>
              Changes are saved automatically
            </p>
            <Button
              type='button'
              label='Close'
              icon={mdiClose}
              color='light'
              onClick={onCancel}
            />
          </div>
        ) : (
          <div className='flex items-center justify-end space-x-3'>
            <Button
              type='button'
              label='Cancel'
              icon={mdiClose}
              color='light'
              onClick={onCancel}
              disabled={loading}
            />
            <Button
              type='button'
              label='Update Business'
              icon={mdiCheck}
              color='primary'
              loading={loading}
              disabled={!isValid || !isDirty}
              onClick={handleSubmit(handleFormSubmit)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
